// use-hl-metrics — 30s-Poll von /info `metaAndAssetCtxs` für alle Coins.
//
// useHyperliquidGlobal liefert via WS nur activeAssetCtx für die 5
// SUBSCRIBED_COINS (BTC/ETH/SOL/xyz:GOLD/xyz:SILVER). Für die anderen
// HYPERLIQUID_COINS (BNB/XRP/SUI/TAO/DOGE/AVAX/LINK/ARB/OP) brauchen wir
// REST. Plus REST liefert dayNtlVlm (Vol 24h) das im WS-activeAssetCtx fehlt.

import { useEffect, useState } from 'react';
import { fetchMetaAndAssetCtxs, type HlAssetCtx } from '@/lib/hl-info-api';

const POLL_INTERVAL_MS = 30_000;

export interface UseHlMetricsOptions {
  enabled?: boolean;
  pollIntervalMs?: number;
}

export interface UseHlMetricsReturn {
  metrics: Map<string, HlAssetCtx>;
  loading: boolean;
  /** Convenience: Vol-24h-Lookup pro Coin. null wenn noch nicht geladen. */
  volume24hUsd: (coin: string) => number | null;
  refetch: () => Promise<void>;
}

export function useHlMetrics(options: UseHlMetricsOptions = {}): UseHlMetricsReturn {
  const { enabled = true, pollIntervalMs = POLL_INTERVAL_MS } = options;
  const [metrics, setMetrics] = useState<Map<string, HlAssetCtx>>(new Map());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const poll = async (): Promise<void> => {
      if (cancelled) return;
      setLoading(true);
      try {
        const next = await fetchMetaAndAssetCtxs();
        if (!cancelled && next.size > 0) setMetrics(next);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void poll();
    const id = setInterval(() => void poll(), pollIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, pollIntervalMs]);

  const volume24hUsd = (coin: string): number | null => {
    const m = metrics.get(coin);
    return m ? m.dayNtlVlm : null;
  };

  const refetch = async (): Promise<void> => {
    setLoading(true);
    try {
      const next = await fetchMetaAndAssetCtxs();
      if (next.size > 0) setMetrics(next);
    } finally {
      setLoading(false);
    }
  };

  return { metrics, loading, volume24hUsd, refetch };
}
